import { ArrowPathIcon, ClockIcon } from "@heroicons/react/24/outline";
import { PauseIcon, PlayIcon, StopIcon } from "@heroicons/react/24/solid";
import { DangerButton } from "components/ui/danger-button";
import { fancyAlert } from "components/ui/fancy-alert";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { TextArea } from "components/ui/textarea";
import { formatElapsed, useTimer } from "contexts/timer-context";
import {
    Popover,
    PopoverButton,
    PopoverPanel,
} from "lib/headlessui/components/popover/popover";
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";

export const TimerTrayItem: React.FC = () => {
    const trackTimeFeature = useFeature("trackTime");
    const { settings } = useTenantSettings();

    if (!trackTimeFeature || !settings.trackTimeEnabled) {
        return null;
    }

    return <TimerTrayItemInner />;
};

const TimerTrayItemInner: React.FC = () => {
    const {
        timerState,
        elapsedSeconds,
        clientId,
        clientName,
        pendingAction,
        pauseTimer,
        resumeTimer,
        stopTimer,
        resetTimer,
        saveEntry,
    } = useTimer();
    const isBusy = pendingAction !== null;
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const isActive = timerState !== "idle";

    const handleSave = useCallback(
        async (close: () => void) => {
            if (!note.trim()) return;
            setSaving(true);
            try {
                await saveEntry(note);
                setNote("");
                close();
            } catch {
                await fancyAlert("Error", "Failed to save time entry. Please try again.");
            } finally {
                setSaving(false);
            }
        },
        [note, saveEntry],
    );

    const badgeColor =
        timerState === "running"
            ? "bg-green-500"
            : timerState === "paused"
              ? "bg-amber-500"
              : timerState === "stopped"
                ? "bg-red-500"
                : "";

    const elapsedColor =
        timerState === "running"
            ? "text-green-600"
            : timerState === "paused"
              ? "text-amber-600"
              : "text-gray-600";

    return (
        <Popover className="relative">
            {({ close }) => (
                <>
                    <PopoverButton
                        className="relative flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-zinc-950/5 dark:hover:bg-white/5 focus:outline-none"
                        aria-label="Timer">
                        <ClockIcon className="size-5 text-zinc-500" />
                        {isActive && (
                            <>
                                <span
                                    className={`text-xs font-semibold tracking-tight [font-variant-numeric:tabular-nums] ${elapsedColor}`}>
                                    {formatElapsed(elapsedSeconds)}
                                </span>
                                <span
                                    className={`absolute top-0.5 left-[18px] size-2 rounded-full ${badgeColor} ${timerState === "running" ? "animate-pulse" : ""}`}
                                />
                            </>
                        )}
                    </PopoverButton>
                    <PopoverPanel className="absolute right-0 z-[1400] mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg bg-white shadow-lg ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10 p-4">
                        {timerState === "idle" && (
                            <p className="text-sm text-zinc-500">No active timer</p>
                        )}
                        {timerState === "running" && (
                            <div className="space-y-3">
                                <div>
                                    <Link
                                        to={`/clients/show/${clientId}`}
                                        onClick={() => close()}
                                        className="text-sm font-medium text-brand-600 hover:text-brand-800 truncate block">
                                        {clientName}
                                    </Link>
                                    <div className="text-lg font-semibold tracking-tight [font-variant-numeric:tabular-nums] text-gray-900">
                                        {formatElapsed(elapsedSeconds)}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <SecondaryButton
                                        className="items-center"
                                        onClick={pauseTimer}
                                        disabled={isBusy}>
                                        {pendingAction === "pause" ? (
                                            <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                                        ) : (
                                            <PauseIcon className="h-4 w-4 mr-1" />
                                        )}
                                        Pause
                                    </SecondaryButton>
                                    <DangerButton
                                        className="items-center"
                                        onClick={stopTimer}
                                        disabled={isBusy}>
                                        <StopIcon className="h-4 w-4 mr-1" />
                                        Stop
                                    </DangerButton>
                                </div>
                            </div>
                        )}
                        {timerState === "paused" && (
                            <div className="space-y-3">
                                <div>
                                    <Link
                                        to={`/clients/show/${clientId}`}
                                        onClick={() => close()}
                                        className="text-sm font-medium text-brand-600 hover:text-brand-800 truncate block">
                                        {clientName}
                                    </Link>
                                    <div className="text-lg font-semibold tracking-tight [font-variant-numeric:tabular-nums] text-amber-600">
                                        {formatElapsed(elapsedSeconds)}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <PrimaryButton
                                        className="items-center"
                                        onClick={resumeTimer}
                                        disabled={isBusy}>
                                        {pendingAction === "resume" ? (
                                            <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                                        ) : (
                                            <PlayIcon className="h-4 w-4 mr-1" />
                                        )}
                                        Resume
                                    </PrimaryButton>
                                    <DangerButton
                                        className="items-center"
                                        onClick={stopTimer}
                                        disabled={isBusy}>
                                        <StopIcon className="h-4 w-4 mr-1" />
                                        Stop
                                    </DangerButton>
                                </div>
                            </div>
                        )}
                        {timerState === "stopped" && (
                            <div className="space-y-3">
                                <div>
                                    <Link
                                        to={`/clients/show/${clientId}`}
                                        onClick={() => close()}
                                        className="text-sm font-medium text-brand-600 hover:text-brand-800 truncate block">
                                        {clientName}
                                    </Link>
                                    <div className="text-lg font-semibold tracking-tight [font-variant-numeric:tabular-nums] text-gray-900">
                                        {formatElapsed(elapsedSeconds)}
                                    </div>
                                </div>
                                <TextArea
                                    textSize="sm"
                                    rows={2}
                                    value={note}
                                    onChange={e => setNote(e.target.value)}
                                    placeholder="What was done..."
                                />
                                <div className="flex gap-2">
                                    <PrimaryButton
                                        className="items-center"
                                        onClick={() => handleSave(close)}
                                        disabled={!note.trim() || saving}>
                                        {saving ? "Saving..." : "Save"}
                                    </PrimaryButton>
                                    <SecondaryButton
                                        className="items-center"
                                        onClick={() => {
                                            setNote("");
                                            resetTimer();
                                            close();
                                        }}>
                                        Discard
                                    </SecondaryButton>
                                </div>
                            </div>
                        )}
                    </PopoverPanel>
                </>
            )}
        </Popover>
    );
};
